<template>
  <div class="py-6">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Super Admin Dashboard</h1>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Manage AI models and companies
      </p>
    </div>

    <!-- Tabs -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-6">
      <div class="border-b border-gray-200 dark:border-gray-700">
        <nav class="-mb-px flex space-x-8">
          <button
            @click="activeTab = 'models'"
            :class="[
              activeTab === 'models'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
            ]"
          >
            AI Models
          </button>
          <button
            @click="activeTab = 'companies'"
            :class="[
              activeTab === 'companies'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
            ]"
          >
            Companies
          </button>
        </nav>
      </div>
    </div>

    <!-- Models Tab -->
    <div v-if="activeTab === 'models'" class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-8">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">AI Models</h2>
        <button
          @click="showAddModelModal = true"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
          </svg>
          Add Model
        </button>
      </div>

      <!-- Models List -->
      <div v-if="modelsLoading" class="flex justify-center py-12">
        <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <div v-else-if="models.length === 0" class="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
        <p class="text-gray-500 dark:text-gray-400">No models configured. Add your first AI model.</p>
      </div>

      <div v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="model in models"
          :key="model.id"
          class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ model.name }}</h3>
              <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">{{ model.provider }}</p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-500">{{ model.model_identifier }}</p>
              <p v-if="model.description" class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {{ model.description }}
              </p>
            </div>
            <span
              :class="[
                model.is_active
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
              ]"
            >
              {{ model.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>
          <div class="mt-4 flex space-x-2">
            <button
              @click="editModel(model)"
              class="flex-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Edit
            </button>
            <button
              @click="confirmDeleteModel(model)"
              class="flex-1 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-white dark:bg-gray-700 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Companies Tab -->
    <div v-if="activeTab === 'companies'" class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-8">
      <div class="mb-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">All Companies</h2>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          View and manage all registered companies. Click "Access Company" to view their dashboard.
        </p>
      </div>

      <div v-if="companiesLoading" class="flex justify-center py-12">
        <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <div v-else-if="companies.length === 0" class="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
        <p class="text-gray-500 dark:text-gray-400">No companies registered yet.</p>
      </div>

      <div v-else class="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Company
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Owner
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  AI Model
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Agents
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="company in companies" :key="company.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ company.name }}</div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">{{ company.domain }}</div>
                    <div v-if="company.industry" class="text-xs text-gray-400 dark:text-gray-500">{{ company.industry }}</div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900 dark:text-white">{{ company.owner_name || 'N/A' }}</div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">{{ company.owner_email || 'N/A' }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="[
                      company.subscription_status === 'active' 
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : company.subscription_status === 'trial'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    ]">
                    {{ company.subscription_status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center space-x-2">
                    <select
                      v-if="models.length > 0"
                      :value="company.model_id || ''"
                      @change="(e) => setCompanyModel(company.id, (e.target as HTMLSelectElement).value)"
                      class="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">No Model</option>
                      <option v-for="model in models" :key="model.id" :value="model.id">
                        {{ model.name }}
                      </option>
                    </select>
                    <span v-if="company.model_name" class="text-xs text-gray-600 dark:text-gray-400">
                      {{ company.model_name }}
                    </span>
                    <span v-else class="text-xs text-red-500 dark:text-red-400">
                      Not assigned
                    </span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ company.current_agent_count || 0 }} / {{ company.max_agents || 5 }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    @click="impersonateCompany(company)"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    Access Company
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add/Edit Model Modal -->
    <ModelModal
      :is-open="showAddModelModal || !!modelToEdit"
      :model="modelToEdit"
      @close="closeModelModal"
      @saved="loadModels"
    />

    <!-- Delete Model Confirmation -->
    <DeleteConfirmModal
      v-if="modelToDelete"
      :is-open="!!modelToDelete"
      title="Delete Model"
      :message="`Are you sure you want to delete ${modelToDelete?.name}?`"
      @confirm="handleDeleteModel"
      @cancel="modelToDelete = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { adminService, type AIModel, type Company } from '../services/admin'
import ModelModal from '../components/ModelModal.vue'
import DeleteConfirmModal from '../components/DeleteConfirmModal.vue'

const router = useRouter()
const authStore = useAuthStore()

const activeTab = ref<'models' | 'companies'>('companies')
const models = ref<AIModel[]>([])
const companies = ref<Company[]>([])
const modelsLoading = ref(false)
const companiesLoading = ref(false)
const showAddModelModal = ref(false)
const modelToEdit = ref<AIModel | null>(null)
const modelToDelete = ref<AIModel | null>(null)

const loadModels = async () => {
  modelsLoading.value = true
  try {
    models.value = await adminService.getModels()
  } catch (error) {
    console.error('Failed to load models:', error)
  } finally {
    modelsLoading.value = false
  }
}

const loadCompanies = async () => {
  companiesLoading.value = true
  try {
    companies.value = await adminService.getCompaniesWithModels()
  } catch (error) {
    console.error('Failed to load companies:', error)
  } finally {
    companiesLoading.value = false
  }
}

const editModel = (model: AIModel) => {
  modelToEdit.value = model
}

const closeModelModal = () => {
  showAddModelModal.value = false
  modelToEdit.value = null
}

const confirmDeleteModel = (model: AIModel) => {
  modelToDelete.value = model
}

const handleDeleteModel = async () => {
  if (!modelToDelete.value) return

  try {
    await adminService.deleteModel(modelToDelete.value.id)
    await loadModels()
    modelToDelete.value = null
  } catch (error) {
    console.error('Failed to delete model:', error)
    alert(error instanceof Error ? error.message : 'Failed to delete model')
  }
}

const setCompanyModel = async (companyId: number, modelId: string) => {
  if (!modelId) return

  try {
    await adminService.setCompanyModel(companyId, parseInt(modelId))
    await loadCompanies()
  } catch (error) {
    console.error('Failed to set company model:', error)
    alert(error instanceof Error ? error.message : 'Failed to set company model')
  }
}

const impersonateCompany = async (company: Company) => {
  if (!confirm(`Are you sure you want to access ${company.name} as their admin? You will see everything they see.`)) {
    return
  }

  try {
    // Get the company owner user ID
    const owner = await adminService.getCompanyOwner(company.id)
    
    // Impersonate the owner
    const result = await adminService.impersonateCompany(owner.id.toString())
    
    // Store the original super admin info before impersonating
    const originalSuperAdmin = {
      token: authStore.token,
      user: authStore.user,
      company: authStore.company
    }
    localStorage.setItem('originalSuperAdmin', JSON.stringify(originalSuperAdmin))
    
    // Store the impersonation token using auth store method
    authStore.setAuthData(result.token, result.user, result.company)
    localStorage.setItem('isImpersonating', 'true')
    
    // Redirect to dashboard
    router.push('/dashboard')
  } catch (error) {
    console.error('Failed to impersonate:', error)
    alert(error instanceof Error ? error.message : 'Failed to impersonate company')
  }
}

onMounted(() => {
  loadModels()
  if (activeTab.value === 'companies') {
    loadCompanies()
  }
})

// Watch tab changes
watch(activeTab, (newTab) => {
  if (newTab === 'companies' && companies.value.length === 0) {
    loadCompanies()
  }
})
</script>

